import { useLayoutEffect, useState } from "react";

// The chart root clips everything inside it (overflow: hidden)
const CHART_ROOT_SELECTOR = ".futures-chart";
// Space left between the bottom of a menu and the bottom of the chart
const BOTTOM_GAP_PX = 8;
// Mirrors $chart-phone-max-width and $chart-viewport-gutter in _variables.sass
const PHONE_MEDIA_QUERY = "(max-width: 480px)";
const PHONE_SIDE_GAP_PX = 16;

/**
 * Size of a menu opened inside the chart (E16): it never runs past the chart
 * bottom (its content scrolls instead), and on phones it spans the chart
 * width with PHONE_SIDE_GAP_PX on both sides wherever its button is.
 * The menu is moved with `left`, so it needs `position: relative`.
 * Recounted on window resize.
 *
 * @param {React.RefObject<HTMLElement>} menuRef - the menu inside its popover panel
 * @param {boolean} isOpen
 * @returns {{ maxHeight?: number, left?: number, width?: number }} inline style; empty while closed
 */
const useFitChartMenu = (menuRef, isOpen) => {
  const [style, setStyle] = useState({});

  useLayoutEffect(() => {
    const menu = menuRef.current;
    const chartRoot = menu?.closest(CHART_ROOT_SELECTOR);

    if (!isOpen || !chartRoot) return undefined;

    const measure = () => {
      const chartRect = chartRoot.getBoundingClientRect();
      // The panel is not moved by the menu's `left`, so it gives the natural place
      const panelRect = menu.parentElement.getBoundingClientRect();
      const maxHeight = Math.max(0, Math.floor(chartRect.bottom - panelRect.top - BOTTOM_GAP_PX));

      if (!window.matchMedia(PHONE_MEDIA_QUERY).matches) {
        setStyle({ maxHeight });

        return;
      }

      setStyle({
        maxHeight,
        left: Math.round(chartRect.left + PHONE_SIDE_GAP_PX - panelRect.left),
        width: Math.floor(chartRect.width - 2 * PHONE_SIDE_GAP_PX),
      });
    };

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, [menuRef, isOpen]);

  return isOpen ? style : {};
};

export default useFitChartMenu;
